"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeBalanceTransferFee = computeBalanceTransferFee;
exports.getOfferChargeableTerms = getOfferChargeableTerms;
exports.getOfferRefiPayoffCents = getOfferRefiPayoffCents;
exports.getOfferRefiPositions = getOfferRefiPositions;
exports.getOfferRefiLenderName = getOfferRefiLenderName;
exports.roundCashAdditionalUpToNearestThousand = roundCashAdditionalUpToNearestThousand;
const offerQuote_1 = require("../offerQuote");
const math_1 = require("./math");
const HELOC_REFI_LIEN_POSITION = 2;
// ---------------------------------------------------------------------------
// Compute functions (one per fee type)
// ---------------------------------------------------------------------------
/**
 * Balance transfer fee = rate applied to the BT payoff amount. Additive.
 *
 * Formula: amountCents = Math.round(btPayoffCents * balanceTransferFeeDecimal)
 * Source:  avenBalanceTransferManager.ts
 *            amountWithFees = round(amountRequested * (btFeeRatio + 1))
 *            avenTxnFeeAmount = amountWithFees - amountRequested  (= amountRequested * btFeeRatio)
 * BT fee is charged at transaction time, not factored into line size derivation.
 */
function computeBalanceTransferFee(btPayoffCents, balanceTransferFeeDecimal) {
    if (balanceTransferFeeDecimal >= 1) {
        throw new Error(`balanceTransferFeeDecimal must be a decimal (e.g. 0.025), got ${balanceTransferFeeDecimal}`);
    }
    return {
        balanceTransferFeeDecimal,
        amountCents: (0, math_1.roundDecimalProduct)(btPayoffCents, balanceTransferFeeDecimal),
    };
}
function getHelocRefiLenderName(details) {
    const helocRefiLienDetails = details?.lienRefiDetails?.find((lienRefiDetail) => lienRefiDetail.lienPosition === HELOC_REFI_LIEN_POSITION);
    if (helocRefiLienDetails) {
        return helocRefiLienDetails.lenderName || helocRefiLienDetails.lienInfo.lienLenderName || null;
    }
    return null;
}
/** The refi payoff of an offer, read only from its generic `lienRefiOfferTerms`; throws on a refi offer that has none. */
function getOfferRefiPayoff(offer) {
    return (0, offerQuote_1.getRefiPayoff)({ policyName: offer?.policyName ?? null, identifier: offer?.identifier ?? null, contingencies: offer?.contingencies ?? null, lienRefiOfferTerms: offer?.lienRefiOfferTerms });
}
/** The offer as `quoteOffer` reads it for the cash <-> line math. */
function getOfferChargeableTerms(offer) {
    return {
        policyName: offer.policyName ?? null,
        identifier: offer.identifier ?? null,
        contingencies: offer.contingencies ?? null,
        lienRefiOfferTerms: offer.lienRefiOfferTerms,
        appliedDebtSwapOfferTerms: offer.appliedDebtSwapOfferTerms,
        appliedSelectedDebtSwapPersonalFinancialLiabilityIds: offer.appliedSelectedDebtSwapPersonalFinancialLiabilityIds,
        upfrontDrawConfig: offer.upfrontDrawConfig,
        originationFeePercent: offer.originationFeePercent,
        firstDrawFeePercent: offer.firstDrawFeePercent,
        recordingChargesFixedComponentDollars: offer.recordingChargesFixedComponentDollars,
        recordingChargesVariableComponentDecimal: offer.recordingChargesVariableComponentDecimal,
        effectiveRecordingCharges: offer.effectiveRecordingCharges,
        effectiveSigningCharges: offer.effectiveSigningCharges,
        lineSizePricingBands: offer.lineSizePricingBands,
        fixedTermMonths: offer.fixedTermMonths,
        lineSize: offer.lineSize,
    };
}
/** Principal of every refinanced lien, in cents: no fees, no buffers. Zero for a non-refi offer. */
function getOfferRefiPayoffCents(offer) {
    return getOfferRefiPayoff(offer).totalCents;
}
/**
 * The ordered lien positions an offer refinances (empty = non-refi). Drives the slider's
 * group key so a base offer and each refi variant at the same term + fee form distinct tracks.
 */
function getOfferRefiPositions(offer) {
    return getOfferRefiPayoff(offer)
        .liens.map((lien) => lien.lienPosition)
        .sort((a, b) => a - b);
}
function getOfferRefiLenderName(offer) {
    // Canonical source: the lender names across every refinanced lien, joined for display.
    const refiLiensDetails = offer?.lienRefiOfferTerms?.refiLiensDetails;
    if (refiLiensDetails && refiLiensDetails.length > 0) {
        const lenderNames = refiLiensDetails.map((detail) => detail.lenderName).filter((name) => name !== null);
        return lenderNames.length > 0 ? lenderNames.join(' + ') : null;
    }
    const lenderNameFromHelocRefiDetails = getHelocRefiLenderName(offer?.helocRefiDetails);
    if (lenderNameFromHelocRefiDetails) {
        return lenderNameFromHelocRefiDetails;
    }
    return offer?.secondLienLenderName ?? null;
}
/**
 * Round the net cash floor (the additional amount above the committed payoff) up to the nearest
 * $1,000. The Aven-Cash slider exposes the net cash, where the upfront-fee deduction leaves a
 * non-round amount (e.g. $1,030.74 instead of $1,000). This normalizes the displayed "Cash" back to
 * a clean $1,000 multiple without disturbing the no-refi or payoff-exceeds-floor cases.
 */
function roundCashAdditionalUpToNearestThousand(minCashAndPayoffCents, payoffCents) {
    if (payoffCents <= 0 || minCashAndPayoffCents <= payoffCents) {
        return minCashAndPayoffCents;
    }
    return payoffCents + Math.ceil((minCashAndPayoffCents - payoffCents) / 100000) * 100000;
}
